import {
  Box,
  Checkbox,
  FormControlLabel,
  Stack,
  SxProps,
  Typography,
  ClickAwayListener
} from '@mui/material'
import ExpandMoreIcon from '@mui/icons-material/ExpandMore'
import ExpandLessIcon from '@mui/icons-material/ExpandLess'
import { styled } from '@mui/material/styles'
import React, { ReactElement, useState } from 'react'
import './styles/FilterCard.css'

export interface FilterOption {
  key: string
  isChecked: boolean
  label: string
}

interface FilterCardProps {
  filters: FilterOption[]
  label: string | ReactElement
  onFilterChange?: (selectedFilters: FilterOption[]) => void
  className?: string
  sx?: SxProps
  disabledKeys?: string[]
}

const StyledCheckbox = styled(Checkbox)({
  '& .MuiSvgIcon-root': {
    transition: 'none'
  }
})

export const FilterCard: React.FC<FilterCardProps> = ({
  filters,
  label,
  onFilterChange,
  className,
  sx,
  disabledKeys = []
}) => {
  const [open, setOpen] = useState(false)

  const handleToggle = (): void => {
    setOpen((prev) => !prev)
  }

  const handleClickAway = (): void => {
    setOpen(false)
  }

  return (
    <ClickAwayListener onClickAway={handleClickAway}>
      <Box
        className={`filter-card-container ${className ?? ''}`}
        sx={{
          width: '254px',
          ...sx
        }}
      >
        <Box
          onClick={handleToggle}
          className='filter-header'
          sx={(theme) => ({
            borderTop: `1px solid ${theme.palette.primary.main}`,
            borderRight: `1px solid ${theme.palette.primary.main}`,
            borderLeft: `1px solid ${theme.palette.primary.main}`,
            borderBottom: open ? 'none' : `1px solid ${theme.palette.primary.main}`,
            backgroundColor: 'white'
          })}
        >
          <Typography className='text'>
            {label}
          </Typography>
          {open ? <ExpandLessIcon /> : <ExpandMoreIcon />}
        </Box>

        {open && (
          <Box
            className='filter-dropdown'
            sx={(theme) => ({
              borderRight: `1px solid ${theme.palette.primary.main}`,
              borderLeft: `1px solid ${theme.palette.primary.main}`,
              borderBottom: `1px solid ${theme.palette.primary.main}`,
              backgroundColor: 'white'
            })}
          >
            <Stack direction='column'>
              {filters.map((filter) => (
                <FormControlLabel
                  className='item'
                  sx={(theme) => ({
                    color: `${theme.palette.text.primary} !important`,
                    '&:hover': {
                      backgroundColor: 'rgba(23, 55, 79, 0.10)'
                    }
                  })}
                  key={filter.key}
                  control={
                    <StyledCheckbox
                      disabled={disabledKeys.includes(filter.key)}
                      checked={filter.isChecked}
                      onChange={(event) => {
                        const isChecked = event.target.checked
                        const updatedFilters = filters.map(f =>
                          f.key === filter.key
                            ? { ...f, isChecked }
                            : f
                        )
                        onFilterChange?.(updatedFilters)
                      }}
                      disableRipple
                    />
                  }
                  label={
                    <Typography
                      className='text'
                      sx={{ opacity: disabledKeys.includes(filter.key) ? 0.5 : 1 }}
                    >
                      {filter.label}
                    </Typography>
                  }
                />
              ))}
            </Stack>
          </Box>
        )}
      </Box>
    </ClickAwayListener>
  )
}

export default FilterCard
